import {
  ActionIcon,
  Box,
  Button,
  Center,
  CopyButton,
  Group,
  Image,
  Loader,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCheck,
  IconChecks,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconExternalLink,
  IconPhotoOff,
  IconReload,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { repositoryPos } from "../../_base/_const/_constVar";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../model/MessageResponse";

type ImageItem = {
  url: string;
  name?: string;
  createdDate?: string;
};

/**
 * App giao hàng mỗi lần chụp lưu 1 bản ghi { orderNumber, videoUrl?, imageUrl? }
 * nên API có thể trả mảng chuỗi, mảng object hoặc object bọc mảng. Gom hết về
 * 1 dạng; bản ghi chỉ có video (imageUrl rỗng) sẽ bị lọc bỏ.
 */
const normalizeImages = (raw: any): ImageItem[] => {
  if (!raw) return [];

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.imageUrls)
      ? raw.imageUrls
      : Array.isArray(raw?.urls)
        ? raw.urls
        : Array.isArray(raw?.data)
          ? raw.data
          : [raw];

  return list
    .map((item: any) => {
      if (typeof item === "string") return { url: item.trim() };
      if (!item || typeof item !== "object") return { url: "" };

      return {
        url: String(
          item.imageUrl ??
            item.urlImage ??
            item.url ??
            item.fileUrl ??
            item.link ??
            item.path ??
            "",
        ).trim(),
        name: item.fileName ?? item.name ?? item.title,
        createdDate: item.createdDate ?? item.createDate ?? item.time,
      };
    })
    .filter((item: ImageItem) => item.url);
};

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "";
};

// Ảnh chụp từ iPhone có thể là HEIC/HEIF, chỉ Safari tự hiển thị được
const isHeicUrl = (url: string) => /\.(heic|heif)(?:[?#]|$)/i.test(url);

/**
 * Chrome/Edge/Firefox không đọc được HEIC: tải file về rồi đổi sang JPEG ngay
 * trên trình duyệt. Thư viện (~3 MB) chỉ được tải khi đơn có ảnh HEIC.
 */
const convertHeicToJpegUrl = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Tải ảnh HEIC lỗi: HTTP ${response.status}`);

  const { heicTo } = await import("heic-to");
  const jpeg = await heicTo({
    blob: await response.blob(),
    type: "image/jpeg",
    quality: 0.9,
  });
  return URL.createObjectURL(jpeg);
};

const VIEWER_HEIGHT = "min(60vh, 520px)";

const OrderImageModal = ({
  orderNumber,
  videoChecked,
  onChecked,
}: {
  orderNumber: string;
  videoChecked: string | null;
  onChecked?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState<string[]>([]);
  // url HEIC gốc -> object URL của bản JPEG đã đổi
  const [convertedUrls, setConvertedUrls] = useState<Record<string, string>>(
    {},
  );
  const [isMarkingChecked, setIsMarkingChecked] = useState(false);

  /**
   * Đơn chưa chụp ảnh thì API trả về lỗi/không có data, không phân biệt được
   * với lúc request hỏng. Gộp chung thành "chưa có ảnh" để nhân viên không
   * tưởng hệ thống đang lỗi.
   */
  const fetchImages = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/OrderVideo/get-url-image?OrderNumber=${encodeURIComponent(orderNumber)}`,
        true,
      );

      setImages(response?.success ? normalizeImages(response.data) : []);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setActiveIndex(0);
      setBrokenUrls([]);
      setConvertedUrls({});
      setIsLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const markBroken = useCallback(
    (url: string) =>
      setBrokenUrls((prev) => (prev.includes(url) ? prev : [...prev, url])),
    [],
  );

  // Đổi lần lượt từng ảnh HEIC sang JPEG (tuần tự để không treo máy khi đơn nhiều ảnh)
  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    (async () => {
      for (const { url } of images.filter((image) => isHeicUrl(image.url))) {
        if (cancelled) return;
        try {
          const objectUrl = await convertHeicToJpegUrl(url);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          objectUrls.push(objectUrl);
          setConvertedUrls((prev) => ({ ...prev, [url]: objectUrl }));
        } catch (error) {
          console.error(error);
          if (!cancelled) markBroken(url);
        }
      }
    })();

    // Đóng modal hoặc tải lại danh sách ảnh thì dừng đổi và giải phóng bộ nhớ
    return () => {
      cancelled = true;
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [images, markBroken]);

  const goTo = (index: number) =>
    setActiveIndex((index + images.length) % images.length);

  // Đánh dấu đã kiểm tra xong ảnh giao hàng của đơn (imageChecked = "Y")
  const handleMarkChecked = async () => {
    setIsMarkingChecked(true);

    try {
      const response = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/OrderVideo/update-check-status",
        // Gửi kèm videoChecked hiện tại của đơn để không bị ghi đè
        { orderNumber, imageChecked: "Y", videoChecked },
      );

      // Lỗi HTTP đã được repository thông báo
      if (!response) return;

      if (response.success) {
        NotificationExtension.Success(
          `Đã kiểm tra xong ảnh giao hàng đơn ${orderNumber}`,
        );
        onChecked?.();
        modals.closeAll();
      } else {
        NotificationExtension.Fails(
          response.message || "Cập nhật trạng thái kiểm tra ảnh thất bại !",
        );
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails(
        "Có lỗi khi cập nhật trạng thái kiểm tra ảnh !",
      );
    } finally {
      setIsMarkingChecked(false);
    }
  };

  if (isLoading) {
    return (
      <Stack gap="md" py="xs" aria-busy="true">
        <Skeleton height={20} width={240} radius="sm" />
        <Skeleton height={VIEWER_HEIGHT} radius="md" />
        <Group gap="xs" wrap="nowrap">
          {[0, 1, 2, 3].map((item) => (
            <Skeleton key={item} height={72} width={72} radius="sm" />
          ))}
        </Group>
      </Stack>
    );
  }

  const active = images[activeIndex];
  const hasMany = images.length > 1;
  // Ảnh HEIC chưa đổi xong thì chưa có gì để hiện
  const isConverting = (url: string) =>
    isHeicUrl(url) && !convertedUrls[url] && !brokenUrls.includes(url);
  const displayUrl = (url: string) => convertedUrls[url] ?? url;

  return (
    <Stack gap="md" py="xs">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Đơn hàng <b>{orderNumber}</b>
          {images.length > 0 ? ` - ${images.length} ảnh` : ""}
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconReload size={16} />}
            onClick={fetchImages}
          >
            Tải lại
          </Button>
          {active && (
            <Button
              size="xs"
              color="green"
              leftSection={<IconChecks size={16} />}
              loading={isMarkingChecked}
              onClick={handleMarkChecked}
            >
              Đã kiểm tra xong
            </Button>
          )}
        </Group>
      </Group>

      {!active && (
        <Center mih={160}>
          <Stack align="center" gap="xs">
            <IconPhotoOff
              size={40}
              stroke={1.5}
              color="var(--mantine-color-gray-5)"
            />
            <Text size="sm" c="dimmed">
              Đơn hàng này chưa có ảnh giao hàng.
            </Text>
          </Stack>
        </Center>
      )}

      {active && (
        <>
          <Box
            pos="relative"
            h={VIEWER_HEIGHT}
            style={{
              borderRadius: 8,
              overflow: "hidden",
              background: "var(--mantine-color-dark-7)",
            }}
          >
            {brokenUrls.includes(active.url) ? (
              <Center h="100%">
                <Stack align="center" gap="xs">
                  <IconPhotoOff
                    size={40}
                    stroke={1.5}
                    color="var(--mantine-color-gray-5)"
                  />
                  <Text size="sm" c="gray.4">
                    Không hiển thị được ảnh này.
                  </Text>
                </Stack>
              </Center>
            ) : isConverting(active.url) ? (
              <Center h="100%">
                <Stack align="center" gap="xs">
                  <Loader color="gray.4" />
                  <Text size="sm" c="gray.4">
                    Đang chuyển ảnh iPhone (HEIC) để hiển thị...
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Image
                key={active.url}
                src={displayUrl(active.url)}
                alt={`Ảnh giao hàng ${activeIndex + 1} của đơn ${orderNumber}`}
                fit="contain"
                w="100%"
                h="100%"
                onError={() => markBroken(active.url)}
              />
            )}

            {hasMany && (
              <Group
                justify="space-between"
                px="sm"
                pos="absolute"
                inset={0}
                style={{ pointerEvents: "none" }}
              >
                <Tooltip label="Ảnh trước">
                  <ActionIcon
                    variant="white"
                    color="dark"
                    radius="xl"
                    size="lg"
                    aria-label="Ảnh trước"
                    onClick={() => goTo(activeIndex - 1)}
                    style={{ pointerEvents: "auto" }}
                  >
                    <IconChevronLeft size={20} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Ảnh sau">
                  <ActionIcon
                    variant="white"
                    color="dark"
                    radius="xl"
                    size="lg"
                    aria-label="Ảnh sau"
                    onClick={() => goTo(activeIndex + 1)}
                    style={{ pointerEvents: "auto" }}
                  >
                    <IconChevronRight size={20} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Box>

          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Box style={{ minWidth: 0 }}>
              <Text size="sm" fw={500} truncate>
                {active.name ?? `Ảnh ${activeIndex + 1}/${images.length}`}
              </Text>
              {formatDate(active.createdDate) && (
                <Text size="xs" c="dimmed">
                  {formatDate(active.createdDate)}
                </Text>
              )}
            </Box>

            <Group gap={6} wrap="nowrap">
              <CopyButton value={active.url} timeout={1500}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Đã sao chép" : "Sao chép đường dẫn"}
                  >
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? "teal" : "gray"}
                      onClick={copy}
                    >
                      {copied ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconCopy size={16} />
                      )}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>

              <Tooltip label="Mở ở tab mới">
                <Button
                  size="xs"
                  variant="light"
                  component="a"
                  href={displayUrl(active.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconExternalLink size={16} />
                </Button>
              </Tooltip>
            </Group>
          </Group>

          {hasMany && (
            <ScrollArea type="auto" offsetScrollbars>
              <Group gap="xs" wrap="nowrap">
                {images.map((image, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <UnstyledButton
                      key={`${image.url}-${index}`}
                      aria-label={`Xem ảnh ${index + 1}`}
                      aria-current={isActive}
                      onClick={() => setActiveIndex(index)}
                      style={{
                        flex: "0 0 auto",
                        width: 72,
                        height: 72,
                        borderRadius: 6,
                        overflow: "hidden",
                        border: `2px solid ${
                          isActive
                            ? "var(--mantine-color-blue-6)"
                            : "var(--mantine-color-gray-3)"
                        }`,
                        opacity: isActive ? 1 : 0.7,
                      }}
                    >
                      {brokenUrls.includes(image.url) ? (
                        <Center h="100%" bg="gray.1">
                          <IconPhotoOff
                            size={20}
                            stroke={1.5}
                            color="var(--mantine-color-gray-5)"
                          />
                        </Center>
                      ) : isConverting(image.url) ? (
                        <Center h="100%" bg="gray.1">
                          <Loader size="xs" />
                        </Center>
                      ) : (
                        <Image
                          src={displayUrl(image.url)}
                          alt=""
                          fit="cover"
                          w="100%"
                          h="100%"
                          onError={() => markBroken(image.url)}
                        />
                      )}
                    </UnstyledButton>
                  );
                })}
              </Group>
            </ScrollArea>
          )}
        </>
      )}
    </Stack>
  );
};

export default OrderImageModal;
